import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Compass } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Rise } from './ui/motion';
import './LockedPage.css';

interface LockedPageProps {
  /** Eyebrow above the title (e.g. "Pathways"). */
  eyebrow?: string;
  /** Headline. Defaults to a generic unlock message. */
  title?: string;
  /** Body copy explaining what unlocks it and why. */
  description?: string;
  /** One or two short bullet points naming what lands once unlocked. */
  unlocks?: string[];
}

/**
 * Full-page locked state shown to users who haven't finished the onboarding
 * diagnostic yet, for pages whose content is empty/meaningless until then
 * (Pathways, Progress). Pairs with the sidebar lock + tooltip in Layout.tsx;
 * this is the screen they reach if they navigate to the route directly.
 */
const LockedPage: React.FC<LockedPageProps> = ({
  eyebrow = 'Locked',
  title = 'This unlocks after your diagnostic',
  description = 'Complete your onboarding diagnostic - a short, private baseline conversation - and this page fills in with what Lanura learns about you.',
  unlocks,
}) => {
  const navigate = useNavigate();

  return (
    <div className="locked-page">
      <Rise as="div" className="locked-card-wrap">
        <Card className="locked-card">
          <Rise as="span" index={1} className="locked-card-badge" aria-hidden="true">
            <Lock className="lnr-icon" />
          </Rise>
          <Rise as="div" index={2}>
            <Badge className="locked-card-eyebrow">{eyebrow}</Badge>
          </Rise>
          <Rise as="h1" index={3} className="locked-card-title">
            {title}
          </Rise>
          <Rise as="p" index={4} className="locked-card-desc">
            {description}
          </Rise>

          {Array.isArray(unlocks) && unlocks.length > 0 && (
            <Rise as="ul" index={5} className="locked-card-list" aria-label="What this unlocks">
              {unlocks.map((u, i) => (
                <li key={i} className="locked-card-list-item">
                  <span className="locked-card-list-dot" aria-hidden="true" />
                  {u}
                </li>
              ))}
            </Rise>
          )}

          <Rise as="div" index={6} className="locked-card-actions">
            <Button
              onClick={() => navigate('/baseline-quiz')}
              size="lg"
              className="locked-card-cta"
            >
              <Compass className="lnr-icon" aria-hidden="true" />
              Start your diagnostic
            </Button>
            <p className="locked-card-note">Takes about 5 minutes · Private to you</p>
          </Rise>
        </Card>
      </Rise>
    </div>
  );
};

export default LockedPage;
